'use client';

import Image from 'next/image';
import W8SLogo from '../assets/w8s-logo.svg';
import W8SLogoMobile from '../assets/w8s-logo-mobile.svg';
import styled from '@emotion/styled';
import { useMediaQuery } from '@mui/material';
import { Link } from '@/i18n/routing';
import IconButtonWhite from '@/components/formElements/iconButtons/IconButtonWhite';
import { color } from 'src/theme/tokens';
import MainNavigation from './MainNavigation';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import AccountName from './AccountName';
import IconMenuBurger from '@/assets/icons/IconMenuBurger';
import MobileMenu from './MobileMenu';

const HeaderStyled = styled.header`
    background: ${color.brand[50]};
    justify-content: space-between;
    height: 104px;
    width: 100%;
    align-items: center;
    display: flex;

    @media (max-width: 540px) {
        height: 94px;
    }
`;

const AccountWrapper = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
`;

const AccountButtonWrapper = styled.div`
    height: 100%;
`;

const MenuButtonWrapper = styled.div`
    margin-right: 40px;

    @media (min-width: 1148px) {
        display: none;
    }

    @media (max-width: 540px) {
        margin-right: 24px;
    }
`;

const LogoWrapper = styled.div`
    display: flex;
    padding-left: 40px;
    height: 64px;

    @media (max-width: 1147px) {
        padding-left: 32px;
    }

    @media (max-width: 540px) {
        height: 46px;
        padding-left: 24px;
    }
`;

const MainNavigationWrapper = styled.div`
    margin-right: 80px;
    height: 100%;

    @media (max-width: 540px) {
        margin-right: 0;
    }
`;

const NavigationWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
`;

function Header() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
    const t = useTranslations('Header');
    const isMobile = useMediaQuery(`(max-width: 540px)`);

    return (
        <>
            <HeaderStyled>
                <LogoWrapper>
                    <Link href="/" style={{ display: 'flex', height: '100%' }}>
                        <Image
                            src={isMobile ? W8SLogoMobile : W8SLogo}
                            alt={'W8 Shipping'}
                            priority
                            style={{ height: '100%' }}
                            width={isMobile ? 115 : 129}
                            height={isMobile ? 46 : 64}
                        />
                    </Link>
                </LogoWrapper>
                <NavigationWrapper>
                    <MainNavigationWrapper>
                        <MainNavigation />
                    </MainNavigationWrapper>

                    <MenuButtonWrapper>
                        <IconButtonWhite
                            key={mobileMenuOpen ? 'open' : 'closed'}
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label={mobileMenuOpen ? t('mobileMenuButtonClose') : t('mobileMenuButton')}
                            sx={{
                                width: '46px',
                                height: '46px',
                                backgroundColor: mobileMenuOpen
                                    ? `${color.white[20]} !important`
                                    : 'transparent !important',
                                transition: 'background-color 0.15s ease-in-out',
                                '&:hover': {
                                    backgroundColor: mobileMenuOpen
                                        ? `${color.white[20]} !important`
                                        : 'rgba(255, 255, 255, 0.1) !important',
                                },
                                '&:active': {
                                    backgroundColor: mobileMenuOpen
                                        ? `${color.white[20]} !important`
                                        : 'rgba(255, 255, 255, 0.2) !important',
                                },
                                '&.Mui-focusVisible': {
                                    border: 'none',
                                    outline: 'none',
                                    boxShadow: '0 0 0 2px #fff',
                                    backgroundColor: mobileMenuOpen
                                        ? `${color.white[20]} !important`
                                        : 'transparent !important',
                                },
                                '&:focus-visible': {
                                    border: 'none',
                                    outline: 'none',
                                    boxShadow: '0 0 0 2px #fff',
                                    backgroundColor: mobileMenuOpen
                                        ? `${color.white[20]} !important`
                                        : 'transparent !important',
                                },
                            }}
                        >
                            <IconMenuBurger color={color.white[0]} />
                        </IconButtonWhite>
                        <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
                    </MenuButtonWrapper>

                    <AccountWrapper>
                        <AccountButtonWrapper>
                            <AccountName onMobileMenuClose={() => setMobileMenuOpen(false)} />
                        </AccountButtonWrapper>
                    </AccountWrapper>
                </NavigationWrapper>
            </HeaderStyled>
        </>
    );
}

export default Header;
